import { formatTime } from "@clipboard-health/date-time";
import {
  BottomSheet,
  Illustration,
  Text,
  TextButton,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type HiringReliabilityConfig } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { capitalize } from "lodash";
import pluralize from "pluralize";
import { Link } from "react-router-dom";

import { PLACEMENT_CANDIDATE_BAN_ACTION } from "../../PlacementCandidate/types";
import { useReportInterviewLateness } from "../api/useReportInterviewLateness";
import { useUpdateInterview } from "../api/useUpdateInterview";
import { type PlacementIncludedWorkplace } from "../types/fetchPlacements.schema";

interface InterviewCancelBottomSheetProps {
  isProspectWorkplace: boolean;
  modalState: UseModalState;
  interviewId: string;
  isLateCancellation?: boolean;
  bansCount: number;
  penalty: number;
  isSchedulingPastWorkplaceStartHour: boolean;
  workplaceStartHour?: Date;
  hiringReliability: HiringReliabilityConfig;
  workplaceReliabilityBanRule?: NonNullable<
    PlacementIncludedWorkplace["meta"]
  >["reliabilityBanRule"];
  onCancelInterview: () => Promise<void>;
}

export function InterviewCancelBottomSheet(props: InterviewCancelBottomSheetProps) {
  const {
    isProspectWorkplace,
    modalState,
    interviewId,
    onCancelInterview,
    isLateCancellation,
    bansCount,
    penalty,
    hiringReliability,
    workplaceReliabilityBanRule,
    isSchedulingPastWorkplaceStartHour,
    workplaceStartHour,
  } = props;
  const { showErrorToast, showSuccessToast } = useToast();

  const cancelLabel = isProspectWorkplace ? "walk-in interview" : "interview";

  const eventContext = {
    interviewId,
    workplaceReliabilityBanRule,
    hiringReliability,
    isSchedulingPastWorkplaceStartHour,
    workplaceStartHour,
    isProspectWorkplace,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_CANCEL_MODAL_VIEWED, eventContext);

  const { mutateAsync: cancelInterview, isLoading: isCancelling } = useUpdateInterview({
    onSuccess: async () => {
      showSuccessToast(`${capitalize(cancelLabel)} cancelled`);
      modalState.closeModal();
      await onCancelInterview();
    },
    onError: () => {
      showErrorToast(`An error occurred cancelling ${cancelLabel}`);
    },
  });

  const { mutateAsync: reportLateness, isLoading: isReportingLateness } =
    useReportInterviewLateness({
      onSuccess: () => {
        showSuccessToast(
          "Thanks for letting us know! We gave the facility a heads up. They're looking forward to seeing you soon!"
        );
        modalState.closeModal();
      },
      onError: () => {
        showErrorToast(`An error occurred reporting lateness for ${cancelLabel}`);
      },
    });

  const handleCancel = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_CANCEL_MODAL_ACTIONS, {
      ...eventContext,
      action: "cancel",
    });

    await cancelInterview({
      interviewId,
      attributes: {
        status: InterviewStatus.CANCELLED,
      },
    });
  };

  const handleReportLateness = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_CANCEL_MODAL_ACTIONS, {
      ...eventContext,
      action: "report-lateness",
    });

    await reportLateness({
      interviewId,
    });
  };

  const getDescription = () => {
    if (!isLateCancellation) {
      return undefined;
    }

    if (
      hiringReliability.simplifiedBanEnabled &&
      isDefined(workplaceReliabilityBanRule) &&
      isSchedulingPastWorkplaceStartHour &&
      workplaceStartHour
    ) {
      return `You're trying to cancel your interview after ${formatTime(
        workplaceStartHour
      )}. If you cancel or no-show, you'll be restricted from viewing job openings for ${
        workplaceReliabilityBanRule.restrictedDays
      } days.`;
    }

    const { maxLateCancellationHours, penaltyMappers, penaltyLabelMapper, bansCountLabelMapper } =
      hiringReliability;
    const allowedLateCancellations = Object.values(
      penaltyMappers[PLACEMENT_CANDIDATE_BAN_ACTION.INTERVIEW_CANCELLED]
    ).filter((value) => value === 0).length;

    const newBansCount = bansCount + 1;
    const penaltyLabel = penaltyLabelMapper[penalty] ?? `${penalty} ${pluralize("day", penalty)}`;
    const bansCountLabel = bansCountLabelMapper[newBansCount] ?? newBansCount;

    const baseStatement = `You're trying to cancel less than ${maxLateCancellationHours} hours before your interview. This is your ${bansCountLabel} late cancellation`;

    if (newBansCount <= allowedLateCancellations) {
      const pluralizedLateCancellations = `${pluralize(
        "late cancellation",
        allowedLateCancellations
      )} ${pluralize("is", allowedLateCancellations)}`;
      return `${baseStatement}. Please note, only ${allowedLateCancellations} ${pluralizedLateCancellations} allowed. Any future late cancellations will result in a restriction penalty.`;
    }

    return `${baseStatement}. If you cancel, you'll be restricted from viewing job openings for ${penaltyLabel}.`;
  };

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="destructive"
            size="large"
            isLoading={isCancelling}
            onClick={handleCancel}
          >
            Cancel
          </LoadingButton>
          <TextButton
            LinkComponent={Link}
            size="large"
            sx={{ mt: 1, mb: 3 }}
            disabled={isReportingLateness}
            onClick={handleReportLateness}
          >
            I&apos;m just running late
          </TextButton>
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={modalState.closeModal}
      />
      <Stack gap={7} px={7} pb={8} pt="76px" alignItems="center" justifyContent="center">
        <Illustration type="cancellation" />
        <Text variant="h2" textAlign="center">
          Cancel your {cancelLabel}?
        </Text>
        <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
          {getDescription()}
        </Text>
      </Stack>
    </BottomSheet>
  );
}
